import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/base/Toast";
import { fetchApi } from "@/services/apiService";
import { openWhatsAppChat } from "@/utils/whatsapp";
import CallModal from "@/components/feature/CallModal";

interface Props {
  leadId: number;
  leadName: string;
  leadPhone?: string;
  leadStage?: string;
  leadValue?: string;
}

export default function WhatsAppChatCard({ leadId, leadName, leadPhone, leadStage, leadValue }: Props) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignAgentName, setReassignAgentName] = useState("");
  const [reassignReason, setReassignReason] = useState("");
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [conversation, setConversation] = useState<any | null>(null);
  const [agentsList, setAgentsList] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    // Fetch whatsapp chats
    fetchApi<any[]>('/whatsapp/chats', undefined, [])
      .then((res) => {
        const chats = Array.isArray(res) ? res : (res as any)?.data || [];
        const cleanLeadPhone = leadPhone ? leadPhone.replace(/\D/g, "").slice(-10) : "";
        const match = chats.find((c: any) => {
          if (c.leadId && String(c.leadId) === String(leadId)) return true;
          if (c.contact_name && c.contact_name.toLowerCase() === leadName?.toLowerCase()) return true;
          if (cleanLeadPhone && c.phone && c.phone.replace(/\D/g, "").slice(-10) === cleanLeadPhone) return true;
          return false;
        });
        if (match) setConversation(match);
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    // Fetch agents for reassignment
    fetchApi<any>('/auth/company-user/all', undefined, null)
      .then((res) => {
        const userList = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data?.items)
          ? res.data.items
          : Array.isArray(res?.data)
          ? res.data
          : [];

        const list: { id: string; name: string }[] = [];
        userList.forEach((u: any) => {
          const n = u.name || u.fullName || u.username;
          if (n) list.push({ id: String(u.id || n), name: n });
        });

        const defaultAgents = [
          { id: "1", name: "Rajesh Kumar" },
          { id: "2", name: "Priya Sharma" },
          { id: "3", name: "Amit Patel" },
          { id: "4", name: "Sneha Rao" },
          { id: "5", name: "team" },
          { id: "6", name: "AASIB" },
        ];

        setAgentsList(list.length > 0 ? list : defaultAgents);
      })
      .catch(() => {
        setAgentsList([
          { id: "1", name: "Rajesh Kumar" },
          { id: "2", name: "Priya Sharma" },
          { id: "3", name: "Amit Patel" },
          { id: "4", name: "Sneha Rao" },
          { id: "5", name: "team" },
          { id: "6", name: "AASIB" },
        ]);
      });
  }, [leadId, leadName, leadPhone]);

  const handleStartChat = async () => {
    try {
      await fetchApi('/whatsapp/chats', {
        method: 'POST',
        body: JSON.stringify({
          lead_id: leadId,
          contact_name: leadName,
          phone: leadPhone || '',
          stage: leadStage || 'New Inquiry',
          deal_value: leadValue || '₹0',
        }),
      });
      showToast("Opening WhatsApp inbox...", "info");
    } catch {
      showToast("Opening WhatsApp inbox...", "info");
    }
    navigate(`/whatsapp?leadId=${leadId}`);
  };

  const handleOpenChat = () => {
    navigate(`/whatsapp?leadId=${leadId}`);
  };

  const handleDirectWhatsApp = () => {
    if (!leadPhone) {
      showToast("No phone number attached to this lead", "error");
      return;
    }
    const success = openWhatsAppChat(leadPhone);
    if (success) {
      showToast(`Opening WhatsApp chat for ${leadName} (${leadPhone})`, "success");
    } else {
      showToast("Invalid phone number for WhatsApp", "error");
    }
  };

  const handleReassignConfirm = async () => {
    if (!reassignAgentName) return;
    try {
      if (conversation?.id) {
        await fetchApi(`/whatsapp/chats/${conversation.id}/assign`, {
          method: 'PATCH',
          body: JSON.stringify({
            toAgent: reassignAgentName,
            fromAgent: assignedAgent || 'Unassigned',
            reason: reassignReason || 'Reassigned from Lead Detail',
          }),
        });
      }
      showToast(`Chat successfully reassigned to ${reassignAgentName}`, "success");
      setConversation((prev: any) => (prev ? { ...prev, assigned_agent: reassignAgentName, assignedAgentName: reassignAgentName } : prev));
      setShowReassignModal(false);
      setReassignAgentName("");
      setReassignReason("");
    } catch (err: any) {
      showToast(err?.message || "Failed to reassign chat", "error");
    }
  };

  const formatTime = (timeVal?: any) => {
    if (!timeVal) return "Recently";
    const str = String(timeVal);
    if (str.includes("AM") || str.includes("PM")) return str;
    const d = new Date(timeVal);
    if (isNaN(d.getTime())) return "Recently";
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-[#e5e7eb] p-4 text-center py-6 text-xs text-gray-400">
        <i className="ri-loader-4-line animate-spin text-sm mr-1" /> Loading WhatsApp...
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="bg-white rounded-xl border border-[#e5e7eb] p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-[#111827]">WhatsApp</h4>
          {leadPhone && (
            <span className="text-xs text-green-600 font-medium flex items-center gap-1">
              <i className="ri-phone-line" /> {leadPhone}
            </span>
          )}
        </div>
        <div className="text-center py-6">
          <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
            <i className="ri-message-3-line text-xl text-green-500" />
          </div>
          <p className="text-sm text-[#6b7280] mb-3">No conversation started</p>
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={handleStartChat}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition-colors whitespace-nowrap cursor-pointer shadow-sm"
            >
              <i className="ri-chat-new-line" />
              Inbox Chat
            </button>
            {leadPhone && (
              <>
                <button
                  onClick={() => setCallModalOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-indigo-600 text-indigo-700 hover:bg-indigo-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line" />
                  Call
                </button>
                <button
                  onClick={handleDirectWhatsApp}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-green-600 text-green-700 hover:bg-green-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line" />
                  Direct WhatsApp
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  const assignedAgent = conversation.assigned_agent || conversation.assignedAgentName;
  const lastMessageText = conversation.last_message || conversation.lastMessage || "Conversation active";
  const lastTime = formatTime(conversation.last_message_time || conversation.lastMessageTime);

  return (
    <div className="bg-white rounded-xl border border-[#e5e7eb] p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold text-[#111827]">WhatsApp</h4>
          {leadPhone && (
            <span className="text-xs text-gray-500 font-normal">({leadPhone})</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {leadPhone && (
            <>
              <button
                onClick={() => setCallModalOpen(true)}
                title="Call this lead"
                className="text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors flex items-center gap-0.5 cursor-pointer"
              >
                <i className="ri-phone-line" /> Call
              </button>
              <button
                onClick={handleDirectWhatsApp}
                title="Open WhatsApp Web"
                className="text-xs font-medium text-green-600 hover:text-green-800 transition-colors flex items-center gap-0.5 cursor-pointer"
              >
                <i className="ri-whatsapp-line" /> Direct
              </button>
            </>
          )}
          <button
            onClick={handleOpenChat}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            Open Inbox <i className="ri-arrow-right-up-line ml-0.5" />
          </button>
        </div>
      </div>

      {/* Assigned agent */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {assignedAgent ? (
            <>
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-indigo-700">
                  {assignedAgent.split(" ").map((n: string) => n[0]).join("").toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-[#111827]">{assignedAgent}</p>
                <p className="text-xs text-[#6b7280]">Handling this conversation</p>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-user-unfollow-line text-red-500 text-sm" />
              </div>
              <p className="text-sm text-red-600 font-medium">Unassigned</p>
            </div>
          )}
        </div>
        <button
          onClick={() => setShowReassignModal(true)}
          className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
        >
          Reassign Chat
        </button>
      </div>

      {/* Last message preview */}
      <div className="bg-gray-50 rounded-lg p-3">
        <p className="text-sm text-[#374151] line-clamp-2">
          <span className="font-medium">{conversation.contact_name || leadName}:</span>{" "}
          {lastMessageText}
        </p>
        <p className="text-xs text-[#9ca3af] mt-1 flex items-center justify-between">
          <span>{lastTime}</span>
          {Number(conversation.unread_count || conversation.unread || 0) > 0 && (
            <span className="px-1.5 py-px rounded-full bg-green-500 text-white text-[10px] font-medium">
              {conversation.unread_count || conversation.unread} new
            </span>
          )}
        </p>
      </div>

      {/* Reassign Modal */}
      {showReassignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl w-full max-w-sm shadow-lg animate-slide-in p-6">
            <h3 className="text-base font-semibold text-[#111827] mb-4">Reassign Chat</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Reassign to:</label>
                <select
                  value={reassignAgentName}
                  onChange={(e) => setReassignAgentName(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 cursor-pointer"
                >
                  <option value="">Select agent...</option>
                  {agentsList
                    .filter((a) => a.name !== assignedAgent)
                    .map((a) => (
                      <option key={a.id} value={a.name}>{a.name}</option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Reason <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={reassignReason}
                  onChange={(e) => setReassignReason(e.target.value)}
                  placeholder="e.g. Client requested different agent"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                />
              </div>
            </div>

            <div className="flex items-center gap-2.5 mt-5 justify-end">
              <button
                onClick={() => setShowReassignModal(false)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleReassignConfirm}
                disabled={!reassignAgentName}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                Reassign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Call Modal */}
      <CallModal
        isOpen={callModalOpen}
        onClose={() => setCallModalOpen(false)}
        contactName={leadName}
        phone={leadPhone || ""}
        leadId={leadId}
        agentName={assignedAgent}
      />
    </div>
  );
}